import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'expo-router';
import { Animated, Pressable, StyleSheet, useColorScheme, useWindowDimensions } from 'react-native';
import { PanGestureHandler, type PanGestureHandlerStateChangeEvent, State } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { YStack } from 'tamagui';
// Lucide replaces the hand-rolled ThreadIcon/MatchesIcon/ProfileIcon/
// SettingsIcon components (identical lucide marks).
import {
  MessageSquare as ThreadIcon,
  Settings as SettingsIcon,
  User as ProfileIcon,
  Users as MatchesIcon,
} from 'lucide-react-native';

import { Text } from '@/components/text';
import { useAppDrawer } from '@/hooks/use-app-drawer';
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion';
import { borderWidth, darkColors, duration, lightColors, radius, space } from '@/theme/tokens.generated';

// Issue #80's left-side drawer -- founder, 2026-09-18: "a left side
// revealed menu with settings, profile, and all our sections". A
// custom overlay (RN's built-in Animated, not React Navigation's own
// Drawer navigator) rather than restructuring the route tree: every
// route in (app)/ is currently declared directly on the Tabs navigator
// in (app)/_layout.tsx, several with `href: null` so they stay
// reachable via router.push without a tab of their own (see that
// file's own doc comment) -- wrapping the Tabs in a Drawer navigator
// the "real" way needs moving every one of those files into a nested
// (tabs) group first, a materially bigger and riskier restructure than
// this feature needs. This gets the same user-facing result (a
// slide-out panel listing every section) without touching a single
// route file or the Tabs config's own extensively-tuned safe-area/
// label/badge behavior.
//
// Opened by an edge-swipe gesture, not a visible hamburger button --
// founder, 2026-09-18, after the first version shipped with one: "we
// don't need a sidebar burger menu hint... the default view should be
// sexy". A swipe-from-the-left-edge is the same trigger native iOS/
// Android drawers use with no persistent on-screen affordance at all
// (react-native-gesture-handler, already a dependency; its
// GestureHandlerRootView already wraps the whole app in
// src/app/_layout.tsx). PanGestureHandler (the older ref/callback API,
// not the newer worklet-based Gesture API) on purpose: this component's
// animation already runs on plain RN Animated (not Reanimated, no
// existing precedent for it elsewhere in this app when the drawer was
// first built), and the callback API composes with that directly with
// no worklet/shared-value bridging needed.
//
// Rendered once, inside (app)/_layout.tsx, as a sibling AFTER <Tabs> --
// later siblings paint on top in RN's stacking order, so this covers
// the tab bar too without a manual z-index war.
const DRAWER_WIDTH = 280;

// How far a swipe must travel before it counts as "open the drawer" /
// "close the drawer" rather than an incidental touch or a vertical
// scroll that happened to start near the edge.
const SWIPE_THRESHOLD = 60;
// How wide the invisible edge strip is that starts the open gesture --
// a real device's own edge-swipe hot zone is typically ~20px; this
// stays a plain constant (not a WCAG target-size value) since it's a
// gesture zone, not a discrete tappable control.
const EDGE_ZONE_WIDTH = 24;

const SECTIONS = [
  { href: '/', label: 'Threads', Icon: ThreadIcon },
  { href: '/matches', label: 'Matches', Icon: MatchesIcon },
  { href: '/profile', label: 'Profile', Icon: ProfileIcon },
  { href: '/settings', label: 'Settings', Icon: SettingsIcon },
] as const;

export function AppDrawer() {
  const { open, toggle, close } = useAppDrawer();
  const router = useRouter();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;
  const reducedMotion = usePrefersReducedMotion();

  const panelWidth = Math.min(DRAWER_WIDTH, width * 0.8);
  // useState's lazy initializer, not useRef -- this project's lint
  // config (eslint-plugin-react-hooks' newer react-hooks/refs rule)
  // flags reading .current during render at all, even the common
  // `useRef(init).current` idiom itself. Animated.Value is mutated
  // imperatively via .setValue()/Animated.timing() below, never
  // through setState, so keeping it in state (read freely during
  // render, same as any other stateful value) is the rule-compliant
  // equivalent of "create this object once and keep the same
  // instance across renders."
  const [translateX] = useState(() => new Animated.Value(-panelWidth));
  const [scrimOpacity] = useState(() => new Animated.Value(0));

  useEffect(() => {
    const toValue = open ? 0 : -panelWidth;
    const scrimTo = open ? 1 : 0;

    if (reducedMotion) {
      translateX.setValue(toValue);
      scrimOpacity.setValue(scrimTo);
      return;
    }

    Animated.parallel([
      Animated.timing(translateX, { toValue, duration: duration.base, useNativeDriver: true }),
      Animated.timing(scrimOpacity, { toValue: scrimTo, duration: duration.base, useNativeDriver: true }),
    ]).start();
  }, [open, panelWidth, reducedMotion, translateX, scrimOpacity]);

  // The scrim+panel stay unmounted (not just visually hidden) until the
  // drawer has been opened at least once -- keeps the scrim's
  // pointerEvents from ever swallowing a tap on the real screen
  // underneath on first render, before `open` has ever been true.
  // setState called directly in the render body (guarded, not in an
  // effect) is React's own documented pattern for "adjusting state
  // when a prop changes" -- an effect here would commit the stale
  // (not-yet-open) render first and only flip on the NEXT render,
  // which is exactly the extra-frame flash this project's own lint
  // config (react-hooks/set-state-in-effect) exists to catch.
  //
  // The edge-swipe zone below is NOT gated behind this -- it has to be
  // mounted from the very first render, or a swipe before the drawer's
  // ever been opened once would have nothing to detect it.
  const [everOpened, setEverOpened] = useState(open);
  if (open && !everOpened) setEverOpened(true);

  function go(href: (typeof SECTIONS)[number]['href']) {
    close();
    router.push(href);
  }

  // Opens on a rightward swipe starting near the left edge. Only
  // active while closed (once open, this zone sits UNDER the scrim in
  // paint order -- see the edge strip's own placement below -- so the
  // scrim's Pressable, not this handler, receives the touch). Vertical
  // scrolls are excluded via failOffsetY so this doesn't steal a
  // ScrollView's own gesture near the screen edge.
  function onEdgeSwipe(event: PanGestureHandlerStateChangeEvent) {
    if (event.nativeEvent.state !== State.END) return;
    if (event.nativeEvent.translationX > SWIPE_THRESHOLD && !open) toggle();
  }

  // Closes on a leftward swipe anywhere on the open panel -- the
  // expected symmetric gesture once a drawer is open, alongside the
  // existing tap-the-scrim and tap-a-section closes.
  function onPanelSwipe(event: PanGestureHandlerStateChangeEvent) {
    if (event.nativeEvent.state !== State.END) return;
    if (event.nativeEvent.translationX < -SWIPE_THRESHOLD) close();
  }

  return (
    <>
      <PanGestureHandler activeOffsetX={20} failOffsetY={[-20, 20]} onHandlerStateChange={onEdgeSwipe}>
        <Animated.View
          pointerEvents={open ? 'none' : 'auto'}
          style={[StyleSheet.absoluteFill, { right: undefined, width: EDGE_ZONE_WIDTH }]}
        />
      </PanGestureHandler>

      {everOpened && (
        <>
          <Animated.View
            pointerEvents={open ? 'auto' : 'none'}
            style={[StyleSheet.absoluteFill, { opacity: scrimOpacity, backgroundColor: 'rgba(0,0,0,0.5)' }]}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close menu"
              style={StyleSheet.absoluteFill}
              onPress={close}
            />
          </Animated.View>

          <PanGestureHandler activeOffsetX={-20} failOffsetY={[-20, 20]} onHandlerStateChange={onPanelSwipe}>
            <Animated.View
              pointerEvents={open ? 'auto' : 'none'}
              style={[StyleSheet.absoluteFill, { right: undefined, width: panelWidth, transform: [{ translateX }] }]}
            >
              <YStack
                flex={1}
                // "$surfaceRaised" is not a registered Tamagui theme token --
                // tamagui.config.shared.ts exposes that same value under the
                // key `backgroundStrong` (card.tsx's own "raised surface"
                // primitive uses exactly this), not a literal "surfaceRaised"
                // token. The typo'd token name silently failed to resolve to
                // any real color, so this panel had NO real background in
                // either theme -- it just showed whatever was rendered behind
                // it (the page ground), which is why it
                // looked identical regardless of dark mode (founder-reported,
                // 2026-09-18).
                backgroundColor="$backgroundStrong"
                borderRightWidth={borderWidth.hairline}
                borderRightColor="$borderColor"
                paddingTop={insets.top + 24}
                paddingBottom={insets.bottom + 16}
                paddingHorizontal="$4"
                gap="$1"
              >
                <Text
                  fontFamily="$heading"
                  fontWeight="$semibold"
                  fontSize="$titleS"
                  lineHeight="$titleS"
                  color="$color"
                  pb="$4"
                  pl="$3"
                >
                  Nearkin
                </Text>

                {SECTIONS.map(({ href, label, Icon }) => {
                  const active = href === '/' ? pathname === '/' : pathname.startsWith(href);

                  return (
                    <Pressable
                      key={href}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      aria-current={active ? 'page' : undefined}
                      onPress={() => go(href)}
                      style={({ pressed }) => [
                        {
                          flexDirection: 'row' as const,
                          alignItems: 'center' as const,
                          gap: space[3],
                          minHeight: 48,
                          paddingHorizontal: space[3],
                          borderRadius: radius.md,
                          backgroundColor: active ? colors.interactiveActiveOverlay : 'transparent',
                        },
                        pressed ? { opacity: 0.7 } : null,
                      ]}
                    >
                      <Icon size={20} strokeWidth={1.75} color={active ? colors.actionPrimary : colors.textSecondary} />
                      <Text
                        fontFamily="$body"
                        fontWeight={active ? '600' : '500'}
                        color={active ? '$colorLink' : '$color'}
                      >
                        {label}
                      </Text>
                    </Pressable>
                  );
                })}

                <YStack borderTopWidth={borderWidth.hairline} borderTopColor="$borderColor" mt="$3" pt="$3" gap="$1">
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      close();
                      router.push('/reveal-requests');
                    }}
                    style={({ pressed }) => [
                      {
                        minHeight: 44,
                        justifyContent: 'center' as const,
                        paddingHorizontal: space[3],
                        borderRadius: radius.md,
                      },
                      pressed ? { opacity: 0.7 } : null,
                    ]}
                  >
                    <Text fontFamily="$body" color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                      Reveals & connections
                    </Text>
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      close();
                      router.push('/safety-center');
                    }}
                    style={({ pressed }) => [
                      {
                        minHeight: 44,
                        justifyContent: 'center' as const,
                        paddingHorizontal: space[3],
                        borderRadius: radius.md,
                      },
                      pressed ? { opacity: 0.7 } : null,
                    ]}
                  >
                    <Text fontFamily="$body" color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                      Safety Center
                    </Text>
                  </Pressable>
                </YStack>
              </YStack>
            </Animated.View>
          </PanGestureHandler>
        </>
      )}
    </>
  );
}
